import { isTextNode, isTag, isOneOfTags } from 'anodum';
import insertBreakIntoEmptyEditableElement from './insert-break-into-empty-editable-element';
import isEmptyEditableElement from '../verifiers/is-empty-editable-element';
import splitTextByOffset from './split-text-by-offset';
import getCollapsedNodeAndOffset from './get-collapsed-node-and-offset';
import findFirstTextNode from './find-first-text-node';
import hasNodeContent from '../verifiers/has-node-content';
import splitSiblings from './split-siblings';
import removeEmptyLinksFromListItem from './remove-empty-links-from-list-item';
import removeEmptyDecorations from './remove-empty-decorations';
import {
  DEFAULT_BLOCK_TAG,
  HEADING_ELEMENTS,
  BLOCKQUOTE_ELEMENT,
  BREAK_TAG,
  LIST_ITEM,
} from '../constants/editable-elements';
import setCaretAtTheBeginningOfNode from './set-caret-at-the-beginning-of-node';

const splitElementByCollapsedSelection = (element, shouldChangeLastEmptyTagToParagraph = false) => {
  const [liveNode, anchorOffset] = getCollapsedNodeAndOffset();
  const { ownerDocument: liveDocument } = liveNode;
  const liveTextNode = findFirstTextNode(liveNode);

  if (isEmptyEditableElement(element)) {
    const newElement = element.cloneNode(true);
    element.insertAdjacentElement('beforebegin', newElement);
    return;
  }

  if (!isTextNode(liveNode) && isTag(liveNode, 'br')) {
    const newElement = splitSiblings(element, liveNode, liveNode.cloneNode(false));
    element.insertAdjacentElement('beforebegin', newElement);
    return;
  }

  const [leftText, rightText] = splitTextByOffset(liveTextNode, anchorOffset);
  if (!rightText && isTextNode(liveTextNode.nextSibling)) {
    liveTextNode.nodeValue = liveTextNode.nextSibling.nodeValue;
    liveTextNode.nextSibling.remove();
  } else {
    liveTextNode.nodeValue = rightText;
  }

  const leftTextNode = liveDocument.createTextNode(leftText);
  const newElement = splitSiblings(element, liveTextNode, leftTextNode);

  // In some cases, there is a redundant br in the end of the line.
  // How to reproduce bug without next code:
  // 1. Select all list item and make it bold
  // 2. Set caret in the beginning of list item and press Return
  // 3. Press backspace and move cursor to the end of list item and click return
  // 4. Type text in created list item. It should be bold, but formatting isn't saved
  if (element.innerText && !element.innerText.trim()) {
    const brs = [...element.querySelectorAll('br')];
    if (brs.length === 1) brs[0].remove();
  }

  // trim() is necessary for cases, when markup contains text nodes only with spaces or new line.
  // Without trim creating new item, when caret is set to the beginning of li, is broken
  if (!newElement.innerText.trim()) {
    removeEmptyDecorations(newElement);
    removeEmptyLinksFromListItem(newElement);
    insertBreakIntoEmptyEditableElement(newElement);
  }

  element.insertAdjacentElement('beforebegin', newElement);

  if (!element.innerText) {
    if (
      shouldChangeLastEmptyTagToParagraph &&
      isOneOfTags(element, [...HEADING_ELEMENTS, BLOCKQUOTE_ELEMENT])
    ) {
      const defaultElement = liveDocument.createElement(DEFAULT_BLOCK_TAG);
      element.parentNode.replaceChild(defaultElement, element);
      insertBreakIntoEmptyEditableElement(defaultElement);
      setCaretAtTheBeginningOfNode(defaultElement.querySelector(BREAK_TAG));
    } else {
      removeEmptyDecorations(element);
      removeEmptyLinksFromListItem(element);
      insertBreakIntoEmptyEditableElement(element);
    }

    liveTextNode.remove();
  } else {
    const node = isTag(element, LIST_ITEM) ? element.firstChild : element;
    Array.from(node.childNodes).forEach((child) => {
      if (!hasNodeContent(child)) {
        child.remove();
      }
    });
    setCaretAtTheBeginningOfNode(element);
  }
};

export default splitElementByCollapsedSelection;
